/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'

import { IUser } from 'shared/models/User'
import { useHistory } from 'react-router-dom'
import { nanoid } from '@reduxjs/toolkit'
import { getFullName } from 'shared/utils/user-helper'
import {
  ContextMenu,
  IconDiscoverTune,
  IconMessageCircleHeart,
  IconProfile,
  Label,
  Paragraph,
  useFeedback,
} from '@bbl-digital/snorre-v2'
import { getActiveClient } from 'shared/store/clients/selectors'
import { useSelector } from 'react-redux'

interface IProps {
  user: IUser
  dark?: boolean
  onLogout: () => void
  onGoToProfile: () => void
}

const User: React.FC<IProps> = ({ dark, user, onLogout, onGoToProfile }) => {
  const { setOpen: setFeedbackOpen } = useFeedback()
  const { push } = useHistory()
  const activeClient = useSelector(getActiveClient)
  const imageHash = useMemo(() => nanoid(5), [])

  const imageUrl = useMemo(
    () =>
      `${window.config.api.url}/personimage/user/${user.id}/image?${imageHash}`,
    [imageHash]
  )

  const userImage = useMemo(() => {
    return (
      <img
        src={imageUrl}
        alt="User profile"
        className="w-[36px] h-[36px] rounded-full"
        style={{ objectFit: 'cover' }}
      />
    )
  }, [imageUrl])

  return (
    <ContextMenu
      dark={dark}
      triggerVariant="profile"
      triggerLabel={
        <div className="flex-col items-end justify-end hidden md:flex">
          <Label className="text-sm" weight="semibold">
            {getFullName(user.firstname, user.surname)}
          </Label>
          {activeClient && (
            <Paragraph className="text-sm">{activeClient.clientName}</Paragraph>
          )}
        </div>
      }
      triggerIcon={userImage}
    >
      <ContextMenu.Option
        label="Profil"
        trackingEvent="Profil"
        trackingName="Usermenu - Profil"
        icon={<IconProfile colorVariant="primary" />}
        onClick={onGoToProfile}
      />
      <ContextMenu.Option
        label="Innstillinger"
        trackingEvent="Innstillinger"
        trackingName="Usermenu - Innstillinger"
        icon={<IconDiscoverTune colorVariant="primary" />}
        onClick={() => push('/innstillinger')}
      />
      <ContextMenu.Option
        label="Gi tilbakemelding"
        trackingEvent="Gi tilbakemelding"
        trackingName="Usermenu - Tilbakemelding"
        icon={<IconMessageCircleHeart colorVariant="primary" />}
        onClick={() => setFeedbackOpen(true)}
      />

      <ContextMenu.Spacer />
      <ContextMenu.Option
        label="Logg ut"
        className="justify-center"
        trackingEvent="Logg ut"
        trackingName="Usermenu - Logg ut"
        variant="ghost"
        onClick={onLogout}
      />
    </ContextMenu>
  )
}

export default User
